/* PJC v10 — Panier : modifications par CLÉ de ligne (jamais par numéro), requêtes sérialisées, mise à jour optimiste AVEC retour arrière visible,
   contrôle de response.ok / 422 / stock / réseau / délai, messages français dans des zones aria-live, focus rendu après suppression,
   « Passer au paiement » qui attend les mises à jour en cours. Aucune fausse confirmation : l'état vide n'est affiché qu'une fois le serveur d'accord. */
import { Component } from '@theme/component';
import { fetchConfig, onAnimationEnd, prefersReducedMotion, resetShimmer, startViewTransition } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents, QuantitySelectorUpdateEvent, CartSectionRestoredEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import {
  createViewEventElement,
  CartErrorEvent,
  CartDiscountUpdateEvent,
  CartLinesUpdateEvent,
  CartNoteUpdateEvent,
  StandardEvents,
} from '@shopify/events';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/** Délai avant l'envoi d'un changement de quantité (regroupe les clics rapides sur +/-). */
const DEBOUNCE_MS = 300;
/** Au-delà, la requête est abandonnée et l'état réel du panier est rechargé. */
const REQUEST_TIMEOUT_MS = 15000;
/** Filet de sécurité : le bouton de paiement ne reste jamais bloqué si la navigation n'a pas lieu. */
const CHECKOUT_BUSY_RESET_MS = 12000;

const MESSAGES = {
  rejected: 'Cette quantité n’est pas disponible.',
  untouched: 'Votre panier n’a pas été modifié.',
  tooManyRequests: 'Trop de demandes en peu de temps. Patientez un instant puis réessayez.',
  server: 'Le service est momentanément indisponible. Réessayez dans un instant.',
  network: 'Connexion impossible. Vérifiez votre réseau puis réessayez.',
  timeout: 'La mise à jour met trop de temps à répondre. Réessayez dans un instant.',
  generic: 'Le panier n’a pas pu être mis à jour. Réessayez.',
  removalUnconfirmed: 'La suppression n’a pas pu être confirmée.',
  refreshed: 'Nous avons rechargé votre panier pour afficher son contenu réel.',
  refreshFailed: 'Impossible de vérifier votre panier. Rechargez la page avant de continuer.',
  blocked: 'Vérifiez les messages affichés sur votre panier avant de passer au paiement.',
  redirecting: 'Redirection vers le paiement.',
};

/**
 * @param {unknown} value
 * @returns {value is string}
 */
const isText = (value) => typeof value === 'string' && value.trim() !== '';

/**
 * Raccourcit un titre fournisseur très long pour les annonces vocales.
 * @param {string} title
 * @returns {string}
 */
const shorten = (title) => (title.length > 70 ? `${title.slice(0, 67).trimEnd()}…` : title);

/**
 * Traduit un échec de /cart/change en message lisible.
 * `definitive` : le serveur a clairement refusé (le panier n'a pas changé) ; sinon l'état est incertain.
 *
 * @param {{ response?: Response, payload?: any, error?: any }} outcome
 * @returns {{ message: string, definitive: boolean, code: 'INVALID' | 'SERVICE_UNAVAILABLE' }}
 */
function describeFailure({ response, payload, error }) {
  if (error) {
    const timedOut = error.name === 'AbortError';
    return {
      message: timedOut ? MESSAGES.timeout : MESSAGES.network,
      definitive: false,
      code: 'SERVICE_UNAVAILABLE',
    };
  }

  const status = response?.status ?? 0;
  if (response?.ok) {
    // Réponse 200 mais illisible ou inattendue : l'état réel du panier est incertain.
    return { message: MESSAGES.generic, definitive: false, code: 'SERVICE_UNAVAILABLE' };
  }
  if (status === 429) {
    return { message: MESSAGES.tooManyRequests, definitive: true, code: 'SERVICE_UNAVAILABLE' };
  }
  if (status >= 500) {
    return { message: MESSAGES.server, definitive: false, code: 'SERVICE_UNAVAILABLE' };
  }

  // 4xx (dont 422 : quantité refusée, stock insuffisant) ou 200 avec `errors` : le serveur a refusé, rien n'a changé.
  const detail = [payload?.description, typeof payload?.errors === 'string' ? payload.errors : null].find(isText);
  const base = detail ? detail.trim() : MESSAGES.rejected;
  return { message: `${base} ${MESSAGES.untouched}`, definitive: true, code: 'INVALID' };
}

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
export class CartItemsComponent extends createViewEventElement(Component) {
  /** @type {Set<Promise<void>>} */
  static #pendingQuantityUpdates = new Set();

  /**
   * Includes quantity mutations, their section renders, and control cleanup across all cart instances.
   * @returns {ReadonlyArray<Promise<void>>} A snapshot that cannot modify the shared pending set.
   */
  static get pendingQuantityUpdates() {
    return Array.from(CartItemsComponent.#pendingQuantityUpdates);
  }

  /**
   * Changements de quantité en attente d'envoi (anti-rebond par ligne, indexé par clé de ligne).
   * @type {Map<string, { quantity: number, timer: number }>}
   */
  #scheduled = new Map();

  /** Chaîne de requêtes : une seule modification du panier à la fois, dans l'ordre. */
  #queue = Promise.resolve();

  /**
   * Nombre de requêtes en file ou en vol par ligne.
   * @type {Map<string, number>}
   */
  #busyKeys = new Map();

  /** @type {Promise<any> | null} */
  #pendingCartFetch = null;

  #checkoutBusy = false;
  #releaseSubmit = false;
  #checkoutTimer = 0;
  #announceTimer = 0;

  /**
   * True when the event was dispatched from outside this cart-items-component (e.g.
   * `Shopify.actions.updateCart(...)` from an external app, or the SFAPI default
   * handler). Internal dispatchers (cart-discount-component, cart-note) live inside
   * `this` and either morph the section themselves or don't need a refresh — running
   * a fallback render in that case double-renders and can clobber form state.
   * @param {Event} event
   */
  #isExternalCartUpdate(event) {
    return !(event.target instanceof Node) || !this.contains(event.target);
  }

  /**
   * Refreshes this section when a cart AJAX response cannot include rendered section HTML.
   * @param {Object} [options] - Additional section morph options.
   * @param {boolean} [options.injectStylesheet]
   * @returns {Promise<boolean>} Whether the section now shows the server state.
   */
  async #refreshCartSection(options = {}) {
    try {
      await sectionRenderer.renderSection(this.sectionId, {
        cache: false,
        mode: this.isDrawer ? 'hydration' : 'full',
        ...options,
      });
      this.#updateCartQuantitySelectorButtonStates();
      return true;
    } catch (error) {
      if (error?.name === 'AbortError') return false;

      this.dispatchEvent(
        new CartErrorEvent({
          error: error?.message || 'Failed to refresh cart',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
      return false;
    }
  }

  /** @param {CartDiscountUpdateEvent} event */
  #handleDiscountUpdate = (event) => {
    const isExternalCartUpdate = this.#isExternalCartUpdate(event);

    event.promise
      ?.then(({ detail }) => {
        if (!isExternalCartUpdate) return;

        const sectionsHtml = detail?.sections?.[this.sectionId];
        if (sectionsHtml) {
          sectionRenderer.abortRender(this.sectionId);
          morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
          this.#updateCartQuantitySelectorButtonStates();
        } else {
          // External caller (Shopify.actions.updateCart or SFAPI default handler) didn't
          // attach sections; refetch so the discount UI reflects the post-mutation cart.
          this.#refreshCartSection();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  /** @param {CartNoteUpdateEvent} event */
  #handleNoteUpdate = (event) => {
    // Internal cart-note dispatches don't need a section refresh — the user typed the
    // value and the textarea retains it. Only external callers need the UI synced.
    if (!this.#isExternalCartUpdate(event)) return;
    event.promise
      ?.then(({ detail }) => {
        const sections = /** @type {Record<string, string> | undefined} */ (detail?.sections);
        const sectionsHtml = sections?.[this.sectionId];
        if (sectionsHtml) {
          sectionRenderer.abortRender(this.sectionId);
          morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
        } else {
          this.#refreshCartSection();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityEvent);
    document.addEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.addEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
    window.addEventListener('pageshow', this.#handlePageShow);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityEvent);
    document.removeEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.removeEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
    window.removeEventListener('pageshow', this.#handlePageShow);

    this.#flushOnDisconnect();
    window.clearTimeout(this.#checkoutTimer);
    window.clearTimeout(this.#announceTimer);
  }

  /**
   * PJC v10 — le composant quitte la page avec des quantités encore en attente d'anti-rebond : elles sont envoyées
   * tout de suite (après la file en cours) au lieu d'être perdues, puis les autres vues du panier sont resynchronisées
   * par un événement émis sur document (le composant, lui, n'est plus rattaché).
   */
  #flushOnDisconnect() {
    const entries = Array.from(this.#scheduled, ([key, { quantity, timer }]) => {
      window.clearTimeout(timer);
      return { key, quantity };
    });
    this.#scheduled.clear();
    if (entries.length === 0) return;

    const sectionId = this.dataset.sectionId ?? '';
    const queue = this.#queue;
    const deferred = CartLinesUpdateEvent.createPromise();
    const event = new CartLinesUpdateEvent({
      action: 'update',
      context: 'cart',
      lines: entries.map(({ key, quantity }) => ({ id: key, quantity })),
      promise: deferred.promise,
    });
    event.promise?.catch(() => {});
    document.dispatchEvent(event);

    (async () => {
      await queue.catch(() => {});
      /** @type {any} */
      let last = null;
      for (const { key, quantity } of entries) {
        const controller = new AbortController();
        const timeout = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
        try {
          const response = await fetch(`${Theme.routes.cart_change_url}`, {
            ...fetchConfig('json', {
              body: JSON.stringify({ id: key, quantity, sections: sectionId, sections_url: window.location.pathname }),
            }),
            signal: controller.signal,
          });
          const payload = await response.json().catch(() => null);
          if (response.ok && Array.isArray(payload?.items)) last = payload;
        } catch {
          // Échec réseau : la resynchronisation ci-dessous recharge l'état réel du panier.
        } finally {
          window.clearTimeout(timeout);
        }
      }

      // Sans réponse exploitable, aucun fragment de section n'est fourni : les vues rechargent le panier réel.
      deferred.resolve({
        cart: last ? CartLinesUpdateEvent.createCartFromAjaxResponse(last) : undefined,
        detail: {
          sections: last?.sections ?? undefined,
          items: last?.items,
          itemCount: Number.isFinite(Number(last?.item_count)) ? Number(last.item_count) : undefined,
          source: 'cart-items-component',
          didError: !last,
        },
      });
    })().catch((error) => deferred.reject(error));
  }

  /**
   * Re-renders the cart section when the page is restored from the back/forward cache.
   *
   * A restored page replays a frozen DOM, so the line items still show whatever the cart
   * held before the shopper navigated away. `cart-icon.js` self-corrects its count bubble
   * on this same event, which is why the bubble and the cart body disagree until a reload.
   *
   * `persisted` alone does not cover Chrome. Chrome refuses the bfcache for any document holding
   * the Sign in with Shop iframe, `/services/login_with_shop/authorize` inside the shadow root of
   * `shopify-account > shop-login`, and every product page carries one. A shopper's Back there
   * rebuilds the document from the HTTP disk cache instead: `persisted` is false and the frozen
   * cart markup renders anyway. Only the navigation entry separates that from an ordinary page
   * view, so it is the second channel. Safari and Firefox do restore the same page and arrive
   * through `persisted`. Measured on os3 across all three browsers, 2026-09-03.
   *
   * Both channels are still needed. A restored document keeps its ORIGINAL navigation entry in
   * Chrome and Safari, reporting `navigate`, so `back_forward` alone would miss every real
   * restore. And `pageshow` fires after `load` on every ordinary navigation, so dropping both
   * guards would put a section fetch on every page view.
   *
   * @param {PageTransitionEvent} event
   */
  #handlePageShow = (event) => {
    // Retour depuis le paiement : le bouton ne doit pas rester en état « chargement ».
    if (event.persisted) this.#setCheckoutBusy(false);

    const navigationEntry = globalThis.performance?.getEntriesByType?.('navigation')[0];
    const navigationType = CartItemsComponent.#isNavigationTiming(navigationEntry) ? navigationEntry.type : undefined;
    if (!event.persisted && navigationType !== 'back_forward') return;
    // `section_id` is optional in `snippets/cart-items-component.liquid` and the `sectionId`
    // getter throws without it. Every other caller reads it inside a promise chain that
    // already has a catch; this one runs straight off a window listener, so it would escape.
    if (!this.dataset.sectionId) return;

    // `cache: false` on both paths. A bfcache restore never fires `load`, so the renderer's cache
    // still holds the pre-navigation copy. A disk-cache Back does fire `load` and seeds the cache
    // from the stale document it just rebuilt. Either way the cached copy is the same stale HTML
    // the DOM already shows.
    sectionRenderer
      .renderSection(this.sectionId, {
        cache: false,
        mode: this.isDrawer ? 'hydration' : 'full',
        // Empty → non-empty adds the cart summary markup, which carries its own stylesheet.
        injectStylesheet: this.isDrawer && this.querySelector('[data-cart-drawer-empty]') !== null,
      })
      .then(() => {
        // Morph swaps the quantity selectors in place, so they never reconnect and their
        // min/max button states stay bound to the pre-restore quantities. A line restored to its
        // minimum would otherwise keep minus enabled: a wrong affordance and a wasted cart mutation.
        this.#updateCartQuantitySelectorButtonStates();
        // No drawer-open event fires on a restore. Kept for listeners of the restore (e.g. the drawer).
        this.dispatchEvent(new CartSectionRestoredEvent());
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] bfcache restore render failed:', error);
      });
  };

  /* ------------------------------------------------------------------ */
  /* Lignes : repérage par CLÉ                                           */
  /* ------------------------------------------------------------------ */

  /**
   * @param {EventTarget | null} node
   * @returns {HTMLTableRowElement | null} La ligne du panier qui contient le nœud.
   */
  #rowOf(node) {
    const row = node instanceof Element ? node.closest('tr[data-key]') : null;
    return row instanceof HTMLTableRowElement && this.contains(row) ? row : null;
  }

  /**
   * @param {string} key
   * @returns {HTMLTableRowElement | null}
   */
  #rowByKey(key) {
    for (const row of this.querySelectorAll('tr[data-key]')) {
      if (row instanceof HTMLTableRowElement && row.dataset.key === key) return row;
    }
    return null;
  }

  /**
   * Lignes imbriquées (composants d'un lot Shopify) rattachées à une ligne parente.
   * @param {string} key
   * @returns {HTMLTableRowElement[]}
   */
  #nestedRows(key) {
    return Array.from(this.querySelectorAll('tr[data-parent-key]')).filter(
      (row) => row instanceof HTMLTableRowElement && row.dataset.parentKey === key
    );
  }

  /**
   * État visuel d'une ligne pendant une requête. `removing` masque la ligne (retour arrière possible), `updating` l'atténue.
   * @param {HTMLTableRowElement} row
   * @param {'removing' | 'updating' | null} state
   */
  #setLineState(row, state) {
    for (const target of [row, ...this.#nestedRows(row.dataset.key ?? '')]) {
      if (state) target.dataset.pjcState = state;
      else delete target.dataset.pjcState;
      target.inert = state === 'removing';
    }
  }

  /** @param {HTMLTableRowElement} row */
  #restoreQuantity(row) {
    const input = row.querySelector('input[type="number"]');
    if (!(input instanceof HTMLInputElement)) return;

    input.value = row.dataset.quantity ?? input.defaultValue;
    const selector = row.querySelector('cart-quantity-selector-component');
    /** @type {any} */ (selector)?.updateButtonStates?.();
  }

  /* ------------------------------------------------------------------ */
  /* Quantités                                                           */
  /* ------------------------------------------------------------------ */

  /** @param {Event} event */
  #handleQuantityEvent = (event) => {
    if (event instanceof QuantitySelectorUpdateEvent) this.#onQuantityChange(event);
  };

  /**
   * Handles QuantitySelectorUpdateEvent change event. La ligne est identifiée par sa clé, lue sur la ligne qui contient le sélecteur.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const row = this.#rowOf(event.target);
    const key = row?.dataset.key;
    if (!row || !key) return;

    const quantity = Number(event.detail.quantity);
    if (!Number.isInteger(quantity) || quantity < 0) {
      this.#restoreQuantity(row);
      return;
    }

    if (quantity === 0) {
      this.#cancelScheduled(key);
      this.updateQuantity({ key, quantity: 0, action: 'clear' });
      return;
    }

    this.#schedule(key, quantity, row);
  }

  /**
   * Planifie l'envoi d'une quantité pour UNE ligne (anti-rebond propre à chaque ligne : deux lignes modifiées à la suite
   * sont toutes les deux envoyées).
   * @param {string} key
   * @param {number} quantity
   * @param {HTMLTableRowElement} row
   */
  #schedule(key, quantity, row) {
    this.#cancelScheduled(key);

    // Retour à la quantité déjà confirmée et aucune requête en cours pour cette ligne : rien à envoyer.
    const confirmed = Number(row.dataset.quantity);
    if (quantity === confirmed && !(this.#busyKeys.get(key) > 0)) return;

    const timer = window.setTimeout(() => this.#flush(key), DEBOUNCE_MS);
    this.#scheduled.set(key, { quantity, timer });
  }

  /** @param {string} key */
  #cancelScheduled(key) {
    const entry = this.#scheduled.get(key);
    if (!entry) return;
    window.clearTimeout(entry.timer);
    this.#scheduled.delete(key);
  }

  /**
   * @param {string} key
   * @returns {Promise<void>}
   */
  #flush(key) {
    const entry = this.#scheduled.get(key);
    if (!entry) return Promise.resolve();

    this.#cancelScheduled(key);
    return this.updateQuantity({ key, quantity: entry.quantity, action: 'change' });
  }

  /**
   * Envoie immédiatement les changements en attente d'anti-rebond.
   * @returns {Promise<PromiseSettledResult<void>[]>}
   */
  flushPending() {
    return Promise.allSettled(Array.from(this.#scheduled.keys(), (key) => this.#flush(key)));
  }

  /**
   * Handles the line item removal (lien « Retirer », qui reste un vrai lien /cart/change sans JavaScript).
   * @param {Event} event
   */
  onLineItemRemove(event) {
    if (event instanceof MouseEvent && (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey)) return;
    event.preventDefault();

    const key = this.#rowOf(event.target)?.dataset.key;
    if (!key) return;

    this.#cancelScheduled(key);
    this.updateQuantity({ key, quantity: 0, action: 'clear' });
  }

  /**
   * Updates the quantity of one line, identified by its line KEY. Requests are serialized.
   * @param {Object} config - The config.
   * @param {string} config.key - The line key (never the line index).
   * @param {number} config.quantity - The quantity (0 removes the line).
   * @param {string} config.action - The action ('change' | 'clear').
   * @returns {Promise<void>}
   */
  updateQuantity(config) {
    const { key } = config;
    this.#busyKeys.set(key, (this.#busyKeys.get(key) ?? 0) + 1);

    // #performUpdate gère ses erreurs ; ce filet empêche un rejet imprévu de bloquer la file ou de remonter non géré.
    const task = this.#queue.then(() => this.#performUpdate(config)).catch((error) => console.error(error));
    this.#queue = task;

    /** @type {Promise<void>} */
    const tracked = task.finally(() => {
      const remaining = (this.#busyKeys.get(key) ?? 1) - 1;
      if (remaining > 0) this.#busyKeys.set(key, remaining);
      else this.#busyKeys.delete(key);
      CartItemsComponent.#pendingQuantityUpdates.delete(tracked);
    });
    CartItemsComponent.#pendingQuantityUpdates.add(tracked);

    return tracked;
  }

  /**
   * @param {Object} config
   * @param {string} config.key
   * @param {number} config.quantity
   * @param {string} config.action
   */
  async #performUpdate({ key, quantity, action }) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${action}:user-action`);
    const deferredUpdatePromise = CartLinesUpdateEvent.createPromise();
    const isRemoval = quantity === 0;
    const row = this.#rowByKey(key);
    const title = row?.dataset.title ?? '';
    const previousQuantity = row ? Number(row.dataset.quantity) : null;
    const focusPlan = isRemoval && row ? this.#planFocusAfterRemoval(row) : null;
    const focusedElement = row?.contains(document.activeElement) ? document.activeElement : null;

    this.#clearMessages();
    this.setAttribute('aria-busy', 'true');
    if (row) {
      this.#setLineState(row, isRemoval ? 'removing' : 'updating');
      /** @type {TextComponent | null} */ (row.querySelector('text-component'))?.shimmer();
    }
    this.refs.cartTotal?.shimmer();

    try {
      const sectionsToUpdate = new Set([this.sectionId]);
      for (const item of document.querySelectorAll('cart-items-component')) {
        if (item instanceof HTMLElement && item.dataset.sectionId) {
          sectionsToUpdate.add(item.dataset.sectionId);
        }
      }
      for (const sectionId of sectionsToUpdate) {
        sectionRenderer.abortRender(sectionId);
      }

      const cartUpdateEvent = new CartLinesUpdateEvent({
        action: isRemoval ? 'remove' : 'update',
        context: 'cart',
        lines: [{ id: key, quantity }],
        promise: deferredUpdatePromise.promise,
      });
      // Inline messages and CartErrorEvent already report failures to the shopper.
      // Consume rejection even when no other cart component subscribes to this event.
      cartUpdateEvent.promise?.catch(() => {});
      this.dispatchEvent(cartUpdateEvent);

      /** @type {{ response?: Response, payload?: any, error?: any }} */
      let outcome;
      try {
        outcome = await this.#requestChange({
          id: key,
          quantity,
          sections: Array.from(sectionsToUpdate).join(','),
          sections_url: window.location.pathname,
        });
      } catch (error) {
        outcome = { error };
      }

      const { response, payload } = outcome;
      const resultingItem = Array.isArray(payload?.items)
        ? payload.items.find((/** @type {{ key: string }} */ item) => item.key === key)
        : undefined;
      const accepted = Boolean(response?.ok) && Array.isArray(payload?.items) && !payload?.errors;

      if (!accepted) {
        await this.#rollback({
          key,
          failure: describeFailure(outcome),
          previousQuantity,
          focusedElement,
          deferred: deferredUpdatePromise,
        });
        return;
      }

      // Jamais de fausse confirmation : on vérifie ce que le serveur a réellement fait.
      if (isRemoval && resultingItem) {
        await this.#rollback({
          key,
          failure: { message: MESSAGES.removalUnconfirmed, definitive: false, code: 'SERVICE_UNAVAILABLE' },
          previousQuantity,
          focusedElement,
          deferred: deferredUpdatePromise,
        });
        return;
      }
      if (!isRemoval && !resultingItem) {
        await this.#rollback({
          key,
          failure: { message: MESSAGES.generic, definitive: false, code: 'SERVICE_UNAVAILABLE' },
          previousQuantity,
          focusedElement,
          deferred: deferredUpdatePromise,
        });
        return;
      }

      resetShimmer(this);

      const sections = /** @type {Record<string, string> | null | undefined} */ (payload.sections);
      const sectionHTML = sections?.[this.sectionId];
      const parsedItemCount = Number(payload.item_count);
      const itemCount = Number.isFinite(parsedItemCount) ? parsedItemCount : 0;

      // Update data-cart-quantity for all matching variants
      this.#updateQuantitySelectors(payload);

      deferredUpdatePromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(payload),
        detail: {
          sections: sections ?? undefined,
          items: payload.items,
          itemCount,
          source: 'cart-items-component',
          didError: false,
        },
      });

      let synced = true;
      if (sectionHTML) {
        sectionRenderer.abortRender(this.sectionId);
        const render = () => morphSection(this.sectionId, sectionHTML, { mode: this.isDrawer ? 'hydration' : 'full' });

        if (itemCount === 0) {
          await startViewTransition(render, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);
        } else {
          await render();
        }
        this.#updateCartQuantitySelectorButtonStates();
      } else {
        synced = await this.#refreshCartSection();
      }

      if (!synced) {
        this.#showError(key, MESSAGES.refreshFailed);
        return;
      }

      this.#applyFocusPlan(focusPlan);

      const requestedMore = !isRemoval && resultingItem && resultingItem.quantity < quantity;
      if (requestedMore) {
        // Le stock a plafonné la quantité : on le dit, sans prétendre que la demande a été satisfaite.
        const notice = `Seuls ${resultingItem.quantity} exemplaire${resultingItem.quantity > 1 ? 's' : ''} sont disponibles pour cet article.`;
        this.#showError(key, notice);
      }

      this.#announceSuccess({ title, quantity: resultingItem?.quantity ?? 0, isRemoval, itemCount });
    } catch (error) {
      console.error(error);
      deferredUpdatePromise.reject(error);
      await this.#rollback({
        key,
        failure: { message: MESSAGES.generic, definitive: false, code: 'SERVICE_UNAVAILABLE' },
        previousQuantity,
        focusedElement,
        deferred: null,
      });
    } finally {
      this.removeAttribute('aria-busy');
      resetShimmer(this);
      cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
    }
  }

  /**
   * Envoie le changement à /cart/change avec un délai maximal.
   * @param {Record<string, unknown>} body
   * @returns {Promise<{ response: Response, payload: any }>}
   */
  async #requestChange(body) {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

    try {
      const response = await fetch(`${Theme.routes.cart_change_url}`, {
        ...fetchConfig('json', { body: JSON.stringify(body) }),
        signal: controller.signal,
      });
      const payload = await response.json().catch(() => null);

      return { response, payload };
    } finally {
      window.clearTimeout(timeout);
    }
  }

  /**
   * Retour arrière visible : la ligne réapparaît, la quantité revient à sa valeur confirmée, un message explique l'échec.
   * Si l'état du panier est incertain (réseau, délai, erreur serveur), l'état réel est rechargé avant d'afficher le message.
   *
   * @param {Object} config
   * @param {string} config.key
   * @param {{ message: string, definitive: boolean, code: 'INVALID' | 'SERVICE_UNAVAILABLE' }} config.failure
   * @param {number | null} config.previousQuantity
   * @param {Element | null} config.focusedElement - Élément de la ligne qui avait le focus avant la requête.
   * @param {{ reject: (reason?: any) => void } | null} config.deferred
   */
  async #rollback({ key, failure, previousQuantity, focusedElement, deferred }) {
    deferred?.reject(new Error(failure.message));
    resetShimmer(this);

    const row = this.#rowByKey(key);
    if (row) {
      this.#setLineState(row, null);
      if (previousQuantity !== null) this.#restoreQuantity(row);
      // La ligne masquée avait fait perdre le focus : on le rend à l'élément d'origine.
      if (focusedElement instanceof HTMLElement && focusedElement.isConnected) focusedElement.focus();
    }

    this.dispatchEvent(new CartErrorEvent({ error: failure.message, code: failure.code }));

    let message = failure.message;
    if (!failure.definitive) {
      const refreshed = await this.#refreshCartSection();
      message = `${failure.message} ${refreshed ? MESSAGES.refreshed : MESSAGES.refreshFailed}`;
    }

    this.#showError(key, message);
  }

  /* ------------------------------------------------------------------ */
  /* Messages, annonces, focus                                           */
  /* ------------------------------------------------------------------ */

  /**
   * Affiche un message sous la ligne concernée (zone role="alert" toujours présente dans le DOM) ou, à défaut, en tête de panier.
   * @param {string | null} key
   * @param {string} message
   */
  #showError(key, message) {
    const row = key ? this.#rowByKey(key) : null;
    const target = row?.querySelector('[data-pjc-line-error]') ?? this.querySelector('[data-pjc-cart-alert]');

    if (!target) {
      this.#announce(message);
      return;
    }

    target.textContent = message;
    if (row) {
      row.dataset.pjcInvalid = '';
      row.scrollIntoView({ block: 'nearest', behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
    }
  }

  #clearMessages() {
    for (const target of this.querySelectorAll('[data-pjc-line-error], [data-pjc-cart-alert]')) {
      target.textContent = '';
    }
    for (const row of this.querySelectorAll('tr[data-pjc-invalid]')) {
      if (row instanceof HTMLElement) delete row.dataset.pjcInvalid;
    }
  }

  /**
   * Annonce vocale polie (région aria-live toujours présente, voir snippets/cart-items-component.liquid).
   * @param {string} message
   */
  #announce(message) {
    const region = this.querySelector('[data-pjc-cart-live]');
    if (!region) return;

    region.textContent = '';
    window.clearTimeout(this.#announceTimer);
    // Vider puis remplir après un court délai : un message identique au précédent est quand même relu.
    this.#announceTimer = window.setTimeout(() => {
      region.textContent = message;
    }, 60);
  }

  /**
   * @param {Object} config
   * @param {string} config.title
   * @param {number} config.quantity
   * @param {boolean} config.isRemoval
   * @param {number} config.itemCount
   */
  #announceSuccess({ title, quantity, isRemoval, itemCount }) {
    const name = title ? shorten(title) : 'Article';
    const total = this.querySelector('[data-testid="cart-total-value"]')?.textContent?.trim();

    if (isRemoval) {
      const tail = itemCount === 0 ? 'Votre panier est vide.' : total ? `Total du panier : ${total}.` : '';
      this.#announce(`${name} retiré du panier. ${tail}`.trim());
      return;
    }

    this.#announce(`Quantité de ${name} : ${quantity}.${total ? ` Total du panier : ${total}.` : ''}`);
  }

  /**
   * Prépare le focus après une suppression faite au clavier : ligne suivante, sinon précédente, sinon titre de l'état vide.
   * @param {HTMLTableRowElement} row
   * @returns {{ keys: string[] } | null}
   */
  #planFocusAfterRemoval(row) {
    if (!row.contains(document.activeElement)) return null;

    const rows = Array.from(this.querySelectorAll('tr[data-key]')).filter(
      (candidate) => candidate instanceof HTMLTableRowElement && candidate.dataset.parentKey !== row.dataset.key
    );
    const index = rows.indexOf(row);
    const neighbours = [rows[index + 1], rows[index - 1]].filter(
      (candidate) => candidate instanceof HTMLTableRowElement
    );

    return { keys: neighbours.map((candidate) => candidate.dataset.key ?? '').filter(Boolean) };
  }

  /** @param {{ keys: string[] } | null} plan */
  #applyFocusPlan(plan) {
    if (!plan) return;

    const active = document.activeElement;
    if (active && active !== document.body && this.contains(active)) return;

    for (const key of plan.keys) {
      const target = this.#rowByKey(key)?.querySelector('.pjc-line__remove, .pjc-line__title');
      if (target instanceof HTMLElement) {
        target.focus();
        return;
      }
    }

    const heading = document.querySelector('[data-pjc-empty-heading]');
    if (heading instanceof HTMLElement) heading.focus();
  }

  /* ------------------------------------------------------------------ */
  /* Paiement                                                            */
  /* ------------------------------------------------------------------ */

  /**
   * @param {boolean} busy
   */
  #setCheckoutBusy(busy) {
    const button = document.getElementById('checkout');
    this.#checkoutBusy = busy;
    window.clearTimeout(this.#checkoutTimer);

    // aria-busy / aria-disabled et jamais `disabled` : un bouton de soumission désactivé n'envoie pas son nom « checkout ».
    if (button) {
      if (busy) button.setAttribute('aria-busy', 'true');
      else button.removeAttribute('aria-busy');
    }

    if (busy) {
      this.#checkoutTimer = window.setTimeout(() => this.#setCheckoutBusy(false), CHECKOUT_BUSY_RESET_MS);
    }
  }

  /**
   * Soumission du formulaire du panier (bouton « Passer au paiement »). Les changements de quantité encore en attente ou en cours
   * sont terminés avant de partir au paiement ; un message d'erreur resté affiché bloque le départ.
   * @param {SubmitEvent} event
   */
  onCheckoutSubmit(event) {
    if (this.#releaseSubmit) {
      this.#releaseSubmit = false;
      return;
    }

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    if (this.#checkoutBusy) {
      event.preventDefault();
      return;
    }

    this.#setCheckoutBusy(true);

    const waiting = this.#scheduled.size > 0 || CartItemsComponent.#pendingQuantityUpdates.size > 0;
    if (!waiting) {
      this.#announce(MESSAGES.redirecting);
      return;
    }

    event.preventDefault();
    this.#submitAfterUpdates(form);
  }

  /** @param {HTMLFormElement} form */
  async #submitAfterUpdates(form) {
    await this.flushPending();
    while (CartItemsComponent.#pendingQuantityUpdates.size) {
      await Promise.allSettled(CartItemsComponent.pendingQuantityUpdates);
    }
    if (!this.isConnected) return;

    const hasMessage = Array.from(this.querySelectorAll('[data-pjc-line-error], [data-pjc-cart-alert]')).some(
      (target) => (target.textContent ?? '').trim() !== ''
    );
    if (hasMessage) {
      this.#setCheckoutBusy(false);
      this.#announce(MESSAGES.blocked);
      return;
    }

    // Le morph a pu retirer l'état « chargement » du bouton : on le repose avant de partir.
    this.#setCheckoutBusy(true);
    this.#releaseSubmit = true;

    const button = document.getElementById('checkout');
    const submitter = button instanceof HTMLButtonElement && button.form === form ? button : null;

    try {
      if (typeof form.requestSubmit === 'function') {
        // submitter absent : requestSubmit() sans argument soumet le formulaire sans bouton.
        if (submitter) form.requestSubmit(submitter);
        else form.requestSubmit();
      } else if (submitter) {
        // iOS < 16 : pas de requestSubmit(). Un clic sur le bouton déclenche le même événement submit.
        submitter.click();
      } else {
        // Dernier recours : submit() n'émet pas d'événement submit.
        const marker = document.createElement('input');
        marker.type = 'hidden';
        marker.name = 'checkout';
        form.append(marker);
        form.submit();
      }
    } catch (error) {
      console.error(error);
      this.#setCheckoutBusy(false);
    }

    // L'événement submit est émis de façon synchrone et libère le verrou ; s'il n'a pas eu lieu (validation native
    // refusée, exception), le verrou ne doit pas rester armé pour la prochaine soumission.
    this.#releaseSubmit = false;
  }

  /**
   * Handles the cart update.
   *
   * @param {CartLinesUpdateEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event.target === this) return;

    event.promise
      ?.then(async ({ detail }) => {
        const sections = detail?.sections;
        const cartItemsHtml = sections?.[this.sectionId];
        // Animate empty → non-empty in the drawer (possible in squeeze mode
        // where the page is interactive alongside the open drawer). This also
        // needs the response stylesheet because it adds the cart summary markup.
        const wasEmptyCartDrawer = this.isDrawer && this.querySelector('[data-cart-drawer-empty]') !== null;
        /** @type {'hydration' | 'full'} */
        const mode = this.isDrawer ? 'hydration' : 'full';
        const morphOptions = {
          mode,
          injectStylesheet: wasEmptyCartDrawer,
        };

        if (cartItemsHtml) {
          sectionRenderer.abortRender(this.sectionId);
          const existingKeys = new Set(this.refs.cartItemRows?.map((row) => row.dataset.key) ?? []);

          if (wasEmptyCartDrawer) {
            startViewTransition(() => {
              morphSection(this.sectionId, cartItemsHtml, morphOptions);
            }, ['fill-cart-drawer']);
          } else {
            await morphSection(this.sectionId, cartItemsHtml, morphOptions);
          }

          // Animate newly added rows (reverse of the remove animation).
          if (!wasEmptyCartDrawer && !prefersReducedMotion()) {
            for (const row of this.refs.cartItemRows ?? []) {
              if (!existingKeys.has(row.dataset.key)) {
                row.classList.add('adding');
                onAnimationEnd(row, () => row.classList.remove('adding'));
              }
            }
          }

          // Update button states for all cart quantity selectors after morph
          this.#updateCartQuantitySelectorButtonStates();
        } else {
          this.#refreshCartSection(morphOptions);
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  async fetchCartData() {
    if (this.#pendingCartFetch) return this.#pendingCartFetch;

    this.#pendingCartFetch = (async () => {
      const response = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status} ${response.statusText}`);
      const data = await response.json();
      return data;
    })().finally(() => {
      this.#pendingCartFetch = null;
    });

    return this.#pendingCartFetch;
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }

  /**
   * `getEntriesByType` is typed as the `PerformanceEntry` base, which has no `type`, and the DOM
   * lib has no per-string overload for `'navigation'`. `entryType` is the spec's discriminant and
   * lives on the base, so this narrows at runtime instead of asserting the way a cast would.
   *
   * @param {PerformanceEntry | undefined} entry
   * @returns {entry is PerformanceNavigationTiming}
   */
  static #isNavigationTiming(entry) {
    return entry?.entryType === 'navigation';
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
